import { Activity, BellRing, CircleCheck, ScrollText, Siren, Waypoints } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { cn } from '@/lib/utils';
import { api } from '../../api';
import { Badge } from '../../components/Badge';
import { DataTable, Section, StatCard, StatGrid, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { Meter } from '../../components/Meter';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatCount, formatPercent } from '../../lib/format';
import { logsLink, traceSearchLink } from '../../lib/opsQuery';
import { useApi } from '../../lib/useApi';
import { budgetRemaining, budgetTone } from './metrics';
import { AlertRow, IncidentTable, LinkButton, RefreshButton } from './shared';

interface Instance {
  instance: string;
  up: boolean;
}

/** Requests per second by route, for the metrics explorer. */
function requestRateQuery(service: string): string {
  return `sum by (route) (rate(registrar_http_requests_total{service="${service}"}[5m]))`;
}

/** `/ops/services/:name`: one service, its instances, what is firing and its objectives. */
export function ServicePage() {
  const { t } = useTranslation('ops');
  const { name = '' } = useParams();
  useDocumentTitle(name);
  const services = useApi((signal) => api.listOpsServices({ signal }), []);
  const alerts = useApi((signal) => api.listAlerts({ signal }), []);
  const incidents = useApi((signal) => api.listIncidents({ signal }), []);
  const slos = useApi((signal) => api.listSlos({ signal }), []);
  const crumbs = [{ label: t('crumb') }, { label: t('services.title'), to: '/ops/services' }, { label: name }];

  const instances: Instance[] =
    services.status === 'success'
      ? services.data.items.filter((s) => (s.service ?? '') === name).map((s) => ({ instance: s.instance ?? '—', up: s.up }))
      : [];
  const up = instances.filter((i) => i.up).length;
  const mine = alerts.status === 'success' ? alerts.data.items.filter((a) => a.labels.service === name) : [];
  const firing = mine.filter((a) => a.status.state === 'active');
  const linked = incidents.status === 'success' ? incidents.data.items.filter((i) => i.related_services.includes(name)) : [];
  const openIncidents = linked.filter((i) => i.status !== 'RESOLVED').length;
  const objectives = slos.status === 'success' ? slos.data.items.filter((s) => s.service === name) : [];

  const reload = () => {
    services.reload();
    alerts.reload();
    incidents.reload();
    slos.reload();
  };

  const columns: Column<Instance>[] = [
    { key: 'instance', header: t('services.detail.col.instance'), cell: (i) => <code className="text-xs break-all">{i.instance}</code> },
    {
      key: 'state',
      header: t('services.detail.col.state'),
      cell: (i) => <Badge tone={i.up ? 'ok' : 'crit'}>{i.up ? t('services.up') : t('services.down')}</Badge>,
    },
  ];

  return (
    <>
      <PageHeader
        title={name}
        crumbs={crumbs}
        subtitle={t('services.detail.subtitle')}
        actions={
          <>
            <LinkButton to={logsLink(`{service="${name}"}`, '1h')}>
              <ScrollText /> {t('services.detail.logs')}
            </LinkButton>
            <LinkButton to={`/ops/metrics?q=${encodeURIComponent(requestRateQuery(name))}&range=6h`}>
              <Activity /> {t('services.detail.metrics')}
            </LinkButton>
            <LinkButton to={traceSearchLink({ 'service.name': name }, '1h')}>
              <Waypoints /> {t('services.detail.traces')}
            </LinkButton>
            <RefreshButton onClick={reload} busy={services.status === 'loading'} />
          </>
        }
      />
      {services.status === 'loading' ? <Loading rows={3} /> : null}
      {services.status === 'error' ? <DataError error={services.error} onRetry={services.reload} title={t('services.loadFailed')} /> : null}
      {services.status === 'success' && instances.length === 0 ? <EmptyState title={t('services.detail.notFound', { name })} /> : null}
      {services.status === 'success' && instances.length > 0 ? (
        <div className="space-y-6">
          <StatGrid className="xl:grid-cols-3">
            <StatCard
              icon={CircleCheck}
              label={t('services.detail.stat.instances')}
              value={`${formatCount(up)}/${formatCount(instances.length)}`}
              tone={up === instances.length ? 'ok' : up === 0 ? 'crit' : 'warn'}
            />
            <StatCard icon={BellRing} label={t('services.detail.stat.alerts')} value={formatCount(firing.length)} tone={firing.length ? 'warn' : 'ok'} />
            <StatCard icon={Siren} label={t('services.detail.stat.incidents')} value={formatCount(openIncidents)} tone={openIncidents ? 'crit' : 'ok'} />
          </StatGrid>

          <Section title={t('services.detail.instances')} flush>
            <DataTable columns={columns} rows={instances} rowKey={(i) => i.instance} empty={<EmptyState title={t('services.detail.instancesEmpty')} />} />
          </Section>

          <Section title={t('services.detail.alerts')} flush>
            {mine.length === 0 ? (
              <div className="px-5 pb-5">
                <EmptyState title={t('services.detail.alertsEmpty')} />
              </div>
            ) : (
              <ul className="divide-y border-t">
                {mine.map((a) => (
                  <AlertRow key={a.fingerprint} a={a} />
                ))}
              </ul>
            )}
          </Section>

          <Section title={t('services.detail.incidents')} flush>
            <IncidentTable rows={linked} empty={<EmptyState title={t('services.detail.incidentsEmpty')} />} />
          </Section>

          <Section title={t('services.detail.slos')}>
            {objectives.length === 0 ? (
              <EmptyState title={t('services.detail.slosEmpty')} />
            ) : (
              <ul className="flex flex-col gap-3">
                {objectives.map((s) => {
                  const remaining = budgetRemaining(s);
                  const tone = budgetTone(remaining);
                  const label = s.name ?? s.id ?? 'SLO';
                  return (
                    <li key={s.id ?? label} className="space-y-1.5">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
                        <span className="font-medium">{label}</span>
                        <span className={cn('tabular font-semibold', tone === 'crit' && 'text-critical', tone === 'warn' && 'text-warning')}>
                          {remaining === null ? '—' : remaining <= 0 ? t('slo.exhausted') : t('services.detail.budgetLeft', { value: formatPercent(remaining, 0) })}
                        </span>
                      </div>
                      <Meter value={remaining === null ? null : Math.max(0, remaining)} tone={tone} label={t('slo.meterLabel', { name: label })} />
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>
        </div>
      ) : null}
    </>
  );
}
